import React from 'react';
import { language } from '../../services/LanguageService';
import { AsperaWrapper } from '../AsperaWrapper';
import { isMobile } from '../../helpers/helpers';
import { AsperaConnectInstaller } from '../AsperaConnectInstaller';
import { initTransferSystem } from '../../helpers/transfer';
import { SubManager } from '../../classes/SubManager';
import { forceUpdate } from '../../services/ForceUpdateService';
import { AsperaError } from '../../classes/AsperaError';
import { modal } from '../../services/ModalService';
import { tracking } from '../../services/TrackingService';
import { getRunningHttpGatewayTransfers } from '../../helpers/transfer';
import { warnForRunningGateway } from '../../helpers/components';
import { Header, HeaderName, Theme } from '../Carbon';
import { AsperaMobileInstaller } from '../AsperaMobileInstaller';
import { AsperaCustomTheming } from '../AsperaCustomTheming';
/**
 * React Component
 *
 * The AsperaStrictMode provides a wrapper that apps can use for StrictMode support as it is available and working across all Aspera use cases.
 */
export class AsperaStrictMode extends React.Component {
    render() {
        const { children } = this.props;
        return children;
    }
}
/**
 * React Component
 *
 * The AsperaAppWrapper provides a simple way to show a fake top bar and basic loading.
 * This is mostly used on pages that do not have data yet
 *
 * @example
 * ```javascript
 * <AsperaAppWrapper loading={false} error={false}>
 *   <h1>Inner child that shows when loading and error are false</h1>
 * </AsperaAppWrapper>
 * ```
 */
export class AsperaAppWrapper extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            /** Indicate if AppWrapper is loading internal resources */
            internalLoading: true,
        };
        this.subManager = new SubManager();
        this.launchedCaptureModal = false;
    }
    getView() {
        const { error, errorProps, children, productName, customLoadingComponent } = this.props;
        if (!this.isLoading && !error) {
            return children;
        }
        else {
            return (React.createElement("div", null,
                React.createElement(Theme, { theme: "g100", className: "aspera-header" },
                    React.createElement(Header, { "aria-label": productName },
                        React.createElement(HeaderName, { prefix: language.get('globals.ibm') }, productName))),
                React.createElement(AsperaWrapper, { className: "app-wrapper", loading: this.isLoading, customLoadingComponent: customLoadingComponent, error: error, errorProps: errorProps }),
                React.createElement("div", { className: "modal-wrapper" }, modal.modalsToRender.map(item => item))));
        }
    }
    get isLoading() {
        const { loading } = this.props;
        const { internalLoading } = this.state;
        return internalLoading || loading;
    }
    checkTransferLoading() {
        const { loading, error, transferSystemData } = this.props;
        if (!error && !loading) {
            this.subManager.setSubscription(initTransferSystem(transferSystemData.httpGatewayUrl, transferSystemData.ibmId, transferSystemData.transferStorageCallback, transferSystemData.transfersInitialStore, transferSystemData).subscribe({
                next: () => {
                    this.setState({ internalLoading: false });
                },
                error: () => {
                    this.setState({ internalLoading: false });
                },
            }));
        }
        else if (error) {
            // If error is true the app is in a bad state; loading is done
            this.setState({ internalLoading: false });
        }
    }
    componentDidMount() {
        var _a;
        const { sessionTerminationHandle } = this.props;
        (_a = document.getElementById('aspera-offline-loading')) === null || _a === void 0 ? void 0 : _a.remove();
        this.checkTransferLoading();
        tracking.currentTheme = tracking.systemTheme;
        this.subManager.setSubscription(sessionTerminationHandle.subscribe({
            next: redirectUrl => {
                if (this.launchedCaptureModal) {
                    return;
                }
                if (getRunningHttpGatewayTransfers().length) {
                    this.launchedCaptureModal = true;
                    warnForRunningGateway(this.subManager, redirectUrl);
                }
                else {
                    redirectUrl ? window.location.replace(redirectUrl) : window.location.reload();
                }
            },
            error: error => {
                new AsperaError('AsperaAppWrapper: unable to handle session termination', error);
            },
        }));
        this.subManager.setSubscription(forceUpdate.updateSubscription.subscribe({
            next: () => {
                this.setState({});
            },
            error: error => {
                new AsperaError('AsperaAppWrapper: force update subscription unable to load', error);
            },
        }));
        this.subManager.setSubscription(tracking.themeChange.subscribe({
            next: theme => {
                const body = document.body;
                if (body) {
                    body.classList.remove('cds--g100', 'cds--g10');
                    body.classList.add(`cds--${theme}`);
                }
                this.setState({});
            },
            error: error => {
                new AsperaError('AsperaAppWrapper: theme change subscription unable to load', error);
            },
        }));
        this.subManager.setSubscription(modal.modalNotifier.subscribe({
            next: () => {
                const { error } = this.props;
                if (this.isLoading || error)
                    this.setState({});
            },
            error: error => {
                console.error('AsperaAppWrapper: Unable to monitor modals', error);
            },
        }));
    }
    componentDidUpdate(previousProps) {
        const { loading, error } = this.props;
        if (previousProps.loading !== loading || previousProps.error !== error) {
            this.checkTransferLoading();
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, error, productName, transferSystemData, disableConnectInstaller, currentApp } = this.props;
        return (React.createElement("div", { className: `aspera-app-wrapper ${className ? className : ''} ${isMobile() ? 'aspera-mobile-device' : ''}` },
            React.createElement(AsperaCustomTheming, null),
            this.getView(),
            (!this.isLoading && !error && !disableConnectInstaller) && React.createElement(AsperaConnectInstaller, { productName: productName, currentApp: currentApp, transferSystemData: transferSystemData }),
            React.createElement(AsperaMobileInstaller, null)));
    }
}
